import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale, getScaledImageUri } from "@/helpers/image.helpers";
import { WavePodiumItemContentOutcomes } from "./WavePodiumItemContentOutcomes";
import { ApiWaveDecisionWinner } from "@/generated/models/ApiWaveDecisionWinner";
import { motion } from "framer-motion";
import { WaveWinnersPodiumPlaceholder } from "./WaveWinnersPodiumPlaceholder";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
interface WavePodiumItemProps {
  readonly winner?: ApiWaveDecisionWinner;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly position: "first" | "second" | "third";
  readonly customAnimationIndex?: number;
}

// Configuration for position-specific styling with CSS ready classes for Tailwind
const positionStyles = {
  first: {
    color: "#fbbf24",
    // amber-400 - Gold from WinnerDropBadge
    height: "tw-h-[220px]",
    pfpSize: "tw-size-12 md:tw-size-14",
    marginBottom: "-tw-mb-6",
    textColor: "tw-text-[#fbbf24]",
    ring: "tw-ring-[#fbbf24]",
    shadow: "tw-shadow-[0_0_20px_rgba(251,191,36,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base md:tw-text-xl",
    ratingFontSize: "tw-text-sm sm:tw-text-base md:tw-text-2xl",
    positionText: "1st",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#fbbf24]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#fbbf24]/5",
      via: "tw-via-[#fbbf24]/3",
      hover: "group-hover:desktop-hover:tw-from-[#fbbf24]/[0.07]",
      top: "tw-via-[#fbbf24]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#fbbf24]/25",
      sides: "tw-from-[#fbbf24]/10 tw-via-[#fbbf24]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#fbbf24]/25"
    }
  },
  second: {
    color: "#94a3b8",
    // slate-400 - Silver from WinnerDropBadge
    height: "tw-h-[190px]",
    pfpSize: "tw-size-9 md:tw-size-11",
    marginBottom: "-tw-mb-2",
    textColor: "tw-text-[#94a3b8]",
    ring: "tw-ring-[#94a3b8]",
    shadow: "tw-shadow-[0_0_20px_rgba(148,163,184,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base",
    ratingFontSize: "tw-text-sm sm:tw-text-base",
    positionText: "2nd",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#94a3b8]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#94a3b8]/5",
      via: "tw-via-[#94a3b8]/3",
      hover: "group-hover:desktop-hover:tw-from-[#94a3b8]/[0.07]",
      top: "tw-via-[#94a3b8]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#94a3b8]/25",
      sides: "tw-from-[#94a3b8]/10 tw-via-[#94a3b8]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#94a3b8]/25"
    }
  },
  third: {
    color: "#CD7F32",
    // Bronze (same in both components)
    height: "tw-h-[170px]",
    pfpSize: "tw-size-9 md:tw-size-11",
    marginBottom: "-tw-mb-2",
    textColor: "tw-text-[#CD7F32]",
    ring: "tw-ring-[#CD7F32]",
    shadow: "tw-shadow-[0_0_20px_rgba(205,127,50,0.3)]",
    autorFontSize: "tw-text-sm sm:tw-text-base",
    ratingFontSize: "tw-text-base",
    positionText: "3rd",
    bgGradient: "tw-bg-gradient-to-b tw-from-[#CD7F32]/20 tw-to-transparent tw-blur-2xl tw-scale-150",
    gradient: {
      from: "tw-from-[#CD7F32]/5",
      via: "tw-via-[#CD7F32]/3",
      hover: "group-hover:desktop-hover:tw-from-[#CD7F32]/[0.07]",
      top: "tw-via-[#CD7F32]/10",
      hover25: "group-hover:desktop-hover:tw-via-[#CD7F32]/25",
      sides: "tw-from-[#CD7F32]/10 tw-via-[#CD7F32]/5",
      sidesHover: "group-hover:desktop-hover:tw-via-[#CD7F32]/25"
    }
  }
};

// Animation variants for the podium items
const podiumVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.98
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: [0.2, 0.9, 0.3, 1],
      opacity: {
        duration: 0.25
      }
    }
  })
};

// Height mapping for different positions
const heightMap = {
  first: "tw-h-[220px]",
  second: "tw-h-[190px]",
  third: "tw-h-[170px]"
};
export const WavePodiumItem: React.FC<WavePodiumItemProps> = t0 => {
  const $ = _c(99);
  const {
    winner,
    onDropClick,
    position,
    customAnimationIndex
  } = t0;
  const styles = positionStyles[position];
  if (!winner) {
    const t1 = heightMap[position];
    let t2;
    if ($[0] !== position || $[1] !== t1) {
      t2 = <WaveWinnersPodiumPlaceholder height={t1} position={position} />;
      $[0] = position;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const drop = winner.drop as ExtendedDrop;
  const animationIndex = customAnimationIndex !== undefined ? customAnimationIndex : position === "first" ? 0 : position === "second" ? 1 : 2;
  let t1;
  if ($[3] !== drop || $[4] !== onDropClick) {
    t1 = () => onDropClick(drop);
    $[3] = drop;
    $[4] = onDropClick;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = `tw-flex tw-flex-col tw-items-center ${styles.marginBottom} tw-relative tw-z-10`;
  const t3 = `tw-absolute tw-inset-0 ${styles.bgGradient}`;
  let t4;
  if ($[6] !== t3) {
    t4 = <div className={t3} />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `/${drop.author.handle}`;
  let t6;
  if ($[8] !== drop.author.pfp || $[9] !== styles.pfpSize || $[10] !== styles.ring || $[11] !== styles.shadow) {
    t6 = drop.author.pfp ? <img src={getScaledImageUri(drop.author.pfp, ImageScale.W_AUTO_H_50)} alt="" className={`${styles.pfpSize} tw-rounded-xl tw-ring-2 ${styles.ring} tw-object-cover ${styles.shadow}`} /> : <div className={`${styles.pfpSize} tw-rounded-xl tw-ring-2 ${styles.ring} ${styles.shadow} tw-bg-iron-900`} />;
    $[8] = drop.author.pfp;
    $[9] = styles.pfpSize;
    $[10] = styles.ring;
    $[11] = styles.shadow;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <Link href={t5} onClick={_temp} className="tw-transform hover:tw-scale-105 tw-transition-all tw-duration-300">{t6}</Link>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = `tw-w-3 md:tw-size-4 ${styles.textColor}`;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <path fill="currentColor" d="M400 0L176 0c-26.5 0-48.1 21.8-47.1 48.2c.2 5.3 .4 10.6 .7 15.8L24 64C10.7 64 0 74.7 0 88c0 92.6 33.5 157 78.5 200.7c44.3 43.1 98.3 64.8 138.1 75.8c23.4 6.5 39.4 26 39.4 45.6c0 20.9-17 37.9-37.9 37.9L192 448c-17.7 0-32 14.3-32 32s14.3 32 32 32l192 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-26.1 0C337 448 320 431 320 410.1c0-19.6 15.9-39.2 39.4-45.6c39.9-11 93.9-32.7 138.2-75.8C542.5 245 576 180.6 576 88c0-13.3-10.7-24-24-24L446.4 64c.3-5.2 .5-10.4 .7-15.8C448.1 21.8 426.5 0 400 0zM48.9 112l84.4 0c9.1 90.1 29.2 150.3 51.9 190.6c-24.9-11-50.8-26.5-73.2-48.3c-32-31.1-58-76-63-142.3zM464.1 254.3c-22.4 21.8-48.3 37.3-73.2 48.3c22.7-40.3 42.8-100.5 51.9-190.6l84.4 0c-5.1 66.3-31.1 111.2-63 142.3z" />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8) {
    t10 = <svg className={t8} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">{t9}</svg>;
    $[17] = t8;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = `${styles.textColor} tw-font-medium tw-text-xs lg:tw-text-sm`;
  let t12;
  if ($[19] !== styles.positionText || $[20] !== t11) {
    t12 = <span className={t11}>{styles.positionText}</span>;
    $[19] = styles.positionText;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12) {
    t13 = <div className="tw-absolute -tw-bottom-3 tw-inset-x-0 tw-flex tw-justify-center"><div className="tw-bg-iron-900/80 tw-backdrop-blur-sm tw-shadow-lg tw-px-3 tw-py-1 tw-rounded-full tw-flex tw-items-center tw-gap-1.5 tw-border tw-border-iron-700">{t10}{t12}</div></div>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t2 || $[27] !== t4 || $[28] !== t7) {
    t14 = <div className={t2}>{t4}{t7}{t13}</div>;
    $[25] = t13;
    $[26] = t2;
    $[27] = t4;
    $[28] = t7;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = `${styles.height} tw-transition-all tw-duration-300 tw-ease-out tw-bg-gradient-to-b tw-from-iron-900/70 tw-via-iron-900/50 tw-to-transparent tw-backdrop-blur-xl tw-rounded-xl tw-border tw-border-iron-800/60 tw-flex tw-flex-col tw-items-center tw-justify-center tw-shadow-[0_4px_24px_rgba(0,0,0,0.2)] tw-relative tw-overflow-hidden group-hover:desktop-hover:tw-from-iron-900/75 group-hover:desktop-hover:tw-via-iron-900/55 group-hover:desktop-hover:tw-to-transparent group-hover:desktop-hover:tw-border-iron-700/70 group-hover:desktop-hover:tw-shadow-[0_0_48px_rgba(0,0,0,0.35)]`;
  const t16 = `tw-absolute tw-inset-0 tw-bg-gradient-to-b ${styles.gradient.from} ${styles.gradient.via} tw-to-transparent ${styles.gradient.hover}`;
  let t17;
  if ($[30] !== t16) {
    t17 = <div className={t16} />;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = `tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent ${styles.gradient.top} tw-to-transparent ${styles.gradient.hover25}`;
  let t19;
  if ($[32] !== t18) {
    t19 = <div className={t18} />;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = `tw-absolute tw-inset-y-0 tw-right-0 tw-w-px tw-bg-gradient-to-b ${styles.gradient.sides} tw-to-transparent ${styles.gradient.sidesHover}`;
  let t21;
  if ($[34] !== t20) {
    t21 = <div className={t20} />;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const t22 = `tw-absolute tw-inset-y-0 tw-left-0 tw-w-px tw-bg-gradient-to-b ${styles.gradient.sides} tw-to-transparent ${styles.gradient.sidesHover}`;
  let t23;
  if ($[36] !== t22) {
    t23 = <div className={t22} />;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="tw-absolute tw-bottom-0 tw-h-3/4 tw-inset-x-0 tw-bg-gradient-to-t tw-from-black/20 tw-via-black/10 tw-to-transparent" />;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t17 || $[40] !== t19 || $[41] !== t21 || $[42] !== t23) {
    t25 = <div className="tw-absolute tw-inset-0">{t17}{t19}{t21}{t23}{t24}</div>;
    $[39] = t17;
    $[40] = t19;
    $[41] = t21;
    $[42] = t23;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  const t26 = drop.author.handle ?? drop.author.id;
  const t27 = `/${drop.author.handle}`;
  const t28 = `tw-transition-all tw-no-underline tw-mb-2 tw-mt-2 sm:tw-mt-4 tw-relative tw-text-center desktop-hover:hover:${styles.textColor} tw-group/link`;
  const t29 = `${styles.autorFontSize} tw-font-semibold tw-text-iron-200 desktop-hover:hover:${styles.textColor} tw-transition-colors tw-inline-flex tw-items-center`;
  const t30 = `tw-size-3 tw-opacity-0 tw-transition-opacity ${styles.textColor} tw-ml-2 desktop-hover:group-hover/link:tw-opacity-100 tw-absolute tw-left-[100%]`;
  let t31;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" />;
    $[44] = t31;
  } else {
    t31 = $[44];
  }
  let t32;
  if ($[45] !== t30) {
    t32 = <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={t30}>{t31}</svg>;
    $[45] = t30;
    $[46] = t32;
  } else {
    t32 = $[46];
  }
  let t33;
  if ($[47] !== drop.author.handle || $[48] !== t29 || $[49] !== t32) {
    t33 = <span className={t29}>{drop.author.handle}{t32}</span>;
    $[47] = drop.author.handle;
    $[48] = t29;
    $[49] = t32;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== t27 || $[52] !== t28 || $[53] !== t33) {
    t34 = <Link href={t27} onClick={_temp2} className={t28}>{t33}</Link>;
    $[51] = t27;
    $[52] = t28;
    $[53] = t33;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  if ($[55] !== t26 || $[56] !== t34) {
    t35 = <UserProfileTooltipWrapper user={t26}>{t34}</UserProfileTooltipWrapper>;
    $[55] = t26;
    $[56] = t34;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  const t36 = `${drop.rating >= 0 ? styles.textColor : "tw-text-[#ff4466]"} tw-font-semibold ${styles.ratingFontSize}`;
  let t37;
  if ($[58] !== drop.rating) {
    t37 = formatNumberWithCommas(drop.rating);
    $[58] = drop.rating;
    $[59] = t37;
  } else {
    t37 = $[59];
  }
  let t38;
  if ($[60] !== t36 || $[61] !== t37) {
    t38 = <span className={t36}>{t37}</span>;
    $[60] = t36;
    $[61] = t37;
    $[62] = t38;
  } else {
    t38 = $[62];
  }
  let t39;
  if ($[63] !== drop.wave.voting_credit_type) {
    t39 = <span className="tw-text-iron-400 tw-text-xs sm:tw-text-sm">{drop.wave.voting_credit_type}</span>;
    $[63] = drop.wave.voting_credit_type;
    $[64] = t39;
  } else {
    t39 = $[64];
  }
  let t40;
  if ($[65] !== t38 || $[66] !== t39) {
    t40 = <div className="tw-flex tw-items-center tw-gap-x-1">{t38}{t39}</div>;
    $[65] = t38;
    $[66] = t39;
    $[67] = t40;
  } else {
    t40 = $[67];
  }
  let t41;
  if ($[68] !== drop.raters_count) {
    t41 = formatNumberWithCommas(drop.raters_count);
    $[68] = drop.raters_count;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== t41) {
    t42 = <span className="tw-text-iron-200 tw-text-xs sm:tw-text-sm">{t41}</span>;
    $[70] = t41;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  const t43 = drop.raters_count === 1 ? "voter" : "voters";
  let t44;
  if ($[72] !== t43) {
    t44 = <span className="tw-text-iron-400 tw-text-xs sm:tw-text-sm">{t43}</span>;
    $[72] = t43;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  let t45;
  if ($[74] !== t42 || $[75] !== t44) {
    t45 = <div className="tw-flex tw-items-center tw-gap-x-1">{t42}{t44}</div>;
    $[74] = t42;
    $[75] = t44;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  let t46;
  if ($[77] !== winner) {
    t46 = <div onClick={_temp3}><WavePodiumItemContentOutcomes winner={winner} /></div>;
    $[77] = winner;
    $[78] = t46;
  } else {
    t46 = $[78];
  }
  let t47;
  if ($[79] !== t45 || $[80] !== t46) {
    t47 = <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2">{t45}{t46}</div>;
    $[79] = t45;
    $[80] = t46;
    $[81] = t47;
  } else {
    t47 = $[81];
  }
  let t48;
  if ($[82] !== t40 || $[83] !== t47) {
    t48 = <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2 tw-relative">{t40}{t47}</div>;
    $[82] = t40;
    $[83] = t47;
    $[84] = t48;
  } else {
    t48 = $[84];
  }
  let t49;
  if ($[85] !== t15 || $[86] !== t25 || $[87] !== t35 || $[88] !== t48) {
    t49 = <div className="tw-relative tw-w-full"><div className={t15}>{t25}{t35}{t48}</div></div>;
    $[85] = t15;
    $[86] = t25;
    $[87] = t35;
    $[88] = t48;
    $[89] = t49;
  } else {
    t49 = $[89];
  }
  let t50;
  if ($[90] !== t14 || $[91] !== t49) {
    t50 = <div className="tw-flex tw-flex-col tw-items-center">{t14}{t49}</div>;
    $[90] = t14;
    $[91] = t49;
    $[92] = t50;
  } else {
    t50 = $[92];
  }
  let t51;
  if ($[93] !== t1 || $[94] !== t50) {
    t51 = <div onClick={t1} className="tw-cursor-pointer tw-group">{t50}</div>;
    $[93] = t1;
    $[94] = t50;
    $[95] = t51;
  } else {
    t51 = $[95];
  }
  let t52;
  if ($[96] !== animationIndex || $[97] !== t51) {
    t52 = <motion.div variants={podiumVariants} initial="hidden" animate="visible" custom={animationIndex}>{t51}</motion.div>;
    $[96] = animationIndex;
    $[97] = t51;
    $[98] = t52;
  } else {
    t52 = $[98];
  }
  return t52;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}